import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import s from './index.less';
import { motion, useMotionValue } from 'framer-motion';
import ToolBtn from './components/ToolBtn';
import Color from './components/Color';
import { DrawPlugin } from '@/types/whiteboard';
import WhiteBoardTools, { ToolTye } from '../utils';
import { ColorPresets, PEN_WIDTH, ToolValue } from './types';
import Pen from './components/Pen';
import Shape from './components/Shape';
import { onToolsStateChanged } from '@/utils/Bridge/notification/onToolsStateChanged';
import Icon from '@/components/Icon';
import { useSize } from 'ahooks';
import OpenNoteBtn from './components/OpenNoteBtn';
import { note } from '@/models';
import message from '@/components/Message';

const ColorTransform = require('color');
interface NoteProps {
  drawPlugin: any;
}

export const PEN_WidthMap = {
  [PEN_WIDTH.WIDTH_ULTRA_FINE]: 1,
  [PEN_WIDTH.WIDTH_STANDARD]: 2,
  [PEN_WIDTH.WIDTH_BOLD]: 4,
  [PEN_WIDTH.WIDTH_ULTRA_BOLD]: 8,
};
export const PEN_MARKRWidthMap = {
  [PEN_WIDTH.WIDTH_ULTRA_FINE]: 3,
  [PEN_WIDTH.WIDTH_STANDARD]: 6,
  [PEN_WIDTH.WIDTH_BOLD]: 12,
  [PEN_WIDTH.WIDTH_ULTRA_BOLD]: 24,
};
export const Shape_MARKRWidthMap = {
  [PEN_WIDTH.WIDTH_ULTRA_FINE]: 1,
  [PEN_WIDTH.WIDTH_STANDARD]: 2,
  [PEN_WIDTH.WIDTH_BOLD]: 4,
  [PEN_WIDTH.WIDTH_ULTRA_BOLD]: 8,
};

export const NoteToolContext = React.createContext<{ drawPlugin: DrawPlugin | null }>({
  drawPlugin: null,
});
const initData = {
  type: ToolTye.CLOSE,
  pen_strokeWidth: PEN_WIDTH.WIDTH_STANDARD,
  shape_strokeWidth: PEN_WIDTH.WIDTH_STANDARD,
  color: ColorPresets.RED,
};

interface IValue extends ToolValue {
  pen_strokeWidth?: PEN_WIDTH;
  shape_strokeWidth?: PEN_WIDTH;
}
const NoteTools: React.FC<NoteProps> = (props) => {
  const { drawPlugin } = props;
  const ref = useRef(new WhiteBoardTools(drawPlugin));
  // 记录上一次的选择

  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(note?.isWebOpenNote);
  const dragRef = useRef<any>();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [selectTool, setSelectTool] = useState<IValue>(initData);
  const [color, setColor] = useState<string>(initData.color);
  const [canUndo, setCanUndo] = useState<boolean>(false);
  const [canRedo, setCanRedo] = useState<boolean>(false);
  const [activePenType, setActivePenType] = useState<any>(ToolTye.PEN);
  const [activeShapeType, setActiveShapeType] = useState<any>(ToolTye.LINE_ARROW);
  const size = useSize(document.documentElement);
  const dragSize = useSize(dragRef);
  const useOftools = (value: IValue) => {
    //文本颜色和线条颜色 目前都用color, 目前还不会同时出现
    const { type, pen_strokeWidth, shape_strokeWidth, ...config } = value;
    let strokeWidth = PEN_WidthMap[PEN_WIDTH.WIDTH_STANDARD];
    if (type === ToolTye.PEN && pen_strokeWidth) {
      strokeWidth = PEN_WidthMap[pen_strokeWidth];
      setActivePenType(type);
    }
    if (type === ToolTye.PEN_MARKR && pen_strokeWidth) {
      strokeWidth = PEN_MARKRWidthMap[pen_strokeWidth];
      setActivePenType(type);
    }
    if (
      [ToolTye.LINE_ARROW, ToolTye.LINE, ToolTye.RECT, ToolTye.ELLIPSE].includes(type) &&
      shape_strokeWidth
    ) {
      strokeWidth = Shape_MARKRWidthMap[shape_strokeWidth];
      setActiveShapeType(type);
    }
    ref.current.useOftools(type, {
      ...config,
      strokeColor: color,
      color: color,
      strokeWidth: strokeWidth,
    });
  };
  const changeTools = (value: IValue) => {
    const _value = {
      ...selectTool,
      ...value,
    };
    setSelectTool(_value);
    useOftools(_value);
  };

  const onColorChange = (color: string) => {
    setColor(color);
    let strokeColor = color;
    if (selectTool.type === ToolTye.PEN_MARKR) {
      strokeColor = ColorTransform(color)?.alpha(0.5).string();
    }
    ref.current.setClor(strokeColor);
  };
  const hideTools = () => {
    setVisible(false);
    useOftools({ type: ToolTye.CLOSE });
    setSelectTool(initData);
  };

  const openTools = () => {
    setVisible(true);
  };

  useEffect(() => {
    useOftools(initData);
  }, []);

  useEffect(() => {
    onToolsStateChanged((data) => {
      data.toolsState.forEach((item) => {
        if (item.toolType === ToolTye.UNDO) {
          setCanUndo(item.enabled);
        }
        if (item.toolType === ToolTye.REDO) {
          setCanRedo(item.enabled);
        }
      });
    });
  }, []);

  const penValue = useMemo(() => {
    return {
      type: selectTool.type,
      pen_width: selectTool.pen_strokeWidth,
    };
  }, [selectTool]);
  const shapeValue = useMemo(() => {
    return {
      type: selectTool.type,
      pen_width: selectTool.shape_strokeWidth,
    };
  }, [selectTool]);
  const dragConstraints = useMemo(() => {
    if (size?.width && dragSize?.width) {
      return {
        left: -30,
        right: size.width - dragSize?.width,
        bottom: (size.height - dragSize.height) / 2 + dragSize.height - 40,
        top: -(size.height - dragSize.height) / 2 - dragSize.height + 60,
      };
    }
  }, [dragSize, size]);

  return (
    <>
      <motion.div
        drag
        dragElastic={false}
        dragMomentum={false}
        className={s.container}
        initial={{ left: 10 }}
        style={{ x, y, visibility: visible ? 'visible' : 'hidden' }}
        ref={dragRef}
        dragConstraints={dragConstraints}
      >
        <Icon mode="symbol" size={34} name="icon-drag" className={s.dragIcon}></Icon>
        <span className={s.split}></span>

        <ToolBtn
          name="icon-Move"
          active={selectTool.type === ToolTye.SELECT}
          onClick={() =>
            changeTools({
              type: ToolTye.SELECT,
            })
          }
          title={formatMessage({ id: 'select' })}
        ></ToolBtn>
        <Pen
          activeType={activePenType}
          value={penValue}
          onChange={(value) => {
            changeTools({
              type: value.type,
              pen_strokeWidth: value.pen_width,
            });
          }}
        ></Pen>

        <Shape
          activeType={activeShapeType}
          value={shapeValue}
          onChange={(value) => {
            changeTools({
              type: value.type,
              shape_strokeWidth: value.pen_width,
            });
          }}
        ></Shape>

        <ToolBtn
          name="icon-text"
          active={selectTool.type === ToolTye.TEXT}
          onClick={() =>
            changeTools({
              type: ToolTye.TEXT,
            })
          }
          title={formatMessage({ id: 'text' })}
        ></ToolBtn>
        <Color
          disabled={selectTool.type === ToolTye.SELECT || selectTool.type === ToolTye.TEXT}
          value={color}
          onChange={(color) => {
            onColorChange(color);
          }}
          onClick={() => {
            if (selectTool.type === ToolTye.TEXT) {
              message.info({
                content: formatMessage({ id: 'unableChangeColor' }),
                noIcon: true,
              });
            }
          }}
        ></Color>
        <span className={s.split}></span>
        <ToolBtn
          name="icon-eraser"
          title={formatMessage({ id: 'eraser' })}
          onClick={() =>
            changeTools({
              type: ToolTye.ELEMENT_ERASER,
            })
          }
          active={selectTool.type === ToolTye.ELEMENT_ERASER}
        ></ToolBtn>
        <ToolBtn
          name="icon-operate_undo_1"
          title={formatMessage({ id: 'undo' })}
          onClick={() =>
            useOftools({
              type: ToolTye.UNDO,
            })
          }
          disabled={!canUndo}
        ></ToolBtn>
        <ToolBtn
          name="icon-operate_redo_1"
          title={formatMessage({ id: 'redo' })}
          onClick={() => useOftools({ type: ToolTye.REDO })}
          disabled={!canRedo}
        ></ToolBtn>
        <ToolBtn
          name="icon-trash_1"
          title={formatMessage({ id: 'clear' })}
          onClick={() => useOftools({ type: ToolTye.CLEAR })}
        ></ToolBtn>

        <span className={s.split}></span>

        <ToolBtn
          name="icon-leave"
          title={formatMessage({ id: 'close' })}
          onClick={hideTools}
        ></ToolBtn>
      </motion.div>


      <OpenNoteBtn onClick={openTools} visible={!visible }></OpenNoteBtn>
    </>
  );
};

export default NoteTools;
